import { addProtocol, removeProtocol } from 'maplibre-gl'
import { signRequest } from '@/utilities/api'
import { getMaxTextureSize } from '@/utilities/webgl'
import { toast } from '@/utilities/toast'

const PROTOCOL_NAME = 'image-downscale'

let protocolRegistered = false
const warnedSources = new Set<string>()

const downscaleImageBuffer = async (
  sourceBuffer: ArrayBuffer,
  sourceUrl: string
): Promise<ArrayBuffer> => {
  const maxDimension = getMaxTextureSize()

  try {
    const blob = new Blob([sourceBuffer])
    const bitmapSource = await createImageBitmap(blob)

    const { width, height } = bitmapSource

    if (width <= maxDimension && height <= maxDimension) {
      bitmapSource.close()
      return sourceBuffer
    }

    const scale = Math.min(maxDimension / width, maxDimension / height)
    const scaledWidth = Math.round(width * scale)
    const scaledHeight = Math.round(height * scale)

    let outputBlob: Blob

    if (typeof OffscreenCanvas !== 'undefined') {
      const canvas = new OffscreenCanvas(scaledWidth, scaledHeight)
      const context = canvas.getContext('2d')
      if (!context) {
        bitmapSource.close()
        throw new Error('Failed to get OffscreenCanvas 2D context')
      }
      context.drawImage(bitmapSource, 0, 0, scaledWidth, scaledHeight)
      bitmapSource.close()
      outputBlob = await canvas.convertToBlob({ type: 'image/png' })
    } else {
      const canvas = document.createElement('canvas')
      canvas.width = scaledWidth
      canvas.height = scaledHeight
      const context = canvas.getContext('2d')
      if (!context) {
        bitmapSource.close()
        throw new Error('Failed to get canvas 2D context')
      }
      context.drawImage(bitmapSource, 0, 0, scaledWidth, scaledHeight)
      bitmapSource.close()
      outputBlob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(result => {
          if (result) resolve(result)
          else reject(new Error('Canvas toBlob returned null'))
        }, 'image/png')
      })
    }

    if (!warnedSources.has(sourceUrl)) {
      warnedSources.add(sourceUrl)
      toast.info(
        `An image layer (${width}\u00D7${height}px) exceeds your browser's rendering limit of ${maxDimension}px and is being displayed at reduced quality.`
      )
    }

    return outputBlob.arrayBuffer()
  } catch (error) {
    console.warn('Image downscaling failed, returning original buffer:', error)
    return sourceBuffer
  }
}

/**
 * MapLibre protocol handler that intercepts image source requests,
 * fetches the image with AWS SigV4 signing, and downscales it if
 * its dimensions exceed the user's GPU MAX_TEXTURE_SIZE.
 *
 * This prevents the black-box rendering bug where the GPU rejects
 * oversized textures (texImage2D: width or height out of range).
 */
const registerImageSourceProtocol = () => {
  if (protocolRegistered) return

  addProtocol(PROTOCOL_NAME, async (params, abortController) => {
    const { url } = params
    const actualUrl = url.replace(`${PROTOCOL_NAME}://`, '')
    const { url: signedUrl, headers } = signRequest(actualUrl)

    const response = await fetch(signedUrl, {
      signal: abortController.signal,
      headers,
    })

    if (!response.ok) {
      throw new Error(
        `Failed to load image source: ${response.status} ${response.statusText}`
      )
    }

    const arrayBuffer = await response.arrayBuffer()
    const data = await downscaleImageBuffer(arrayBuffer, actualUrl)

    return { data }
  })

  protocolRegistered = true
}

const unregisterImageSourceProtocol = () => {
  if (!protocolRegistered) return
  removeProtocol(PROTOCOL_NAME)
  protocolRegistered = false
}

export {
  registerImageSourceProtocol,
  unregisterImageSourceProtocol,
  PROTOCOL_NAME as IMAGE_SOURCE_PROTOCOL,
}
